import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketApi } from '../../services/api';
import { useAssets } from '../../hooks/useAssets';
import { hideCurrency, gainLossClass, hidePercent } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';

const POPULAR_CRYPTOS = [
  { id: 'bitcoin', symbol: 'BTC', name: 'Bitcoin' },
  { id: 'ethereum', symbol: 'ETH', name: 'Ethereum' },
  { id: 'binancecoin', symbol: 'BNB', name: 'BNB' },
  { id: 'solana', symbol: 'SOL', name: 'Solana' },
  { id: 'cardano', symbol: 'ADA', name: 'Cardano' },
  { id: 'ripple', symbol: 'XRP', name: 'Ripple' },
];

export default function CryptoPage() {
  const { data: cryptoAssets } = useAssets({ type: 'CRYPTO' });
  const [selectedCoin, setSelectedCoin] = useState('bitcoin');
  const { isVisible } = useValuesVisibility();

  const { data: coinPrice } = useQuery({
    queryKey: ['crypto', selectedCoin],
    queryFn: () => marketApi.getCrypto(selectedCoin),
    refetchInterval: 60000, // refresh every 1 minute
  });

  // Calculate portfolio values for crypto assets
  const cryptoPortfolio = cryptoAssets?.map((asset) => {
    const totalInvested = asset.quantity * asset.averagePrice;
    const currentValue = asset.quantity * (asset.currentPrice ?? asset.averagePrice);
    const gainLoss = currentValue - totalInvested;
    const gainLossPercent = totalInvested > 0 ? (gainLoss / totalInvested) * 100 : 0;
    return { ...asset, totalInvested, currentValue, gainLoss, gainLossPercent };
  });

  const totalCryptoInvested = cryptoPortfolio?.reduce((s, a) => s + a.totalInvested, 0) ?? 0;
  const totalCryptoValue = cryptoPortfolio?.reduce((s, a) => s + a.currentValue, 0) ?? 0;
  const totalGainLoss = totalCryptoValue - totalCryptoInvested;

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Criptomoedas</h1>
        <p className="page-subtitle">Análise especializada do portfólio cripto</p>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <span className="stat-label">Total Investido</span>
          <span className="stat-value text-white">{hideCurrency(totalCryptoInvested, isVisible)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Valor Atual</span>
          <span className="stat-value text-white">{hideCurrency(totalCryptoValue, isVisible)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Lucro / Prejuízo</span>
          <span className={`stat-value ${gainLossClass(totalGainLoss)}`}>
            {hideCurrency(totalGainLoss, isVisible)}
          </span>
        </div>
      </div>

      {/* Market Prices */}
      <div className="card">
        <h2 className="text-sm font-semibold text-white mb-4">Preços de Mercado</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {POPULAR_CRYPTOS.map((coin) => (
            <button
              key={coin.id}
              onClick={() => setSelectedCoin(coin.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedCoin === coin.id
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                  : 'bg-white/5 text-white/60 hover:text-white border border-white/5'
              }`}
            >
              {coin.symbol}
            </button>
          ))}
        </div>

        {coinPrice ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-white/40">Preço (BRL)</p>
              <p className="text-lg font-bold text-white font-mono">
                {hideCurrency(coinPrice.priceBRL, isVisible)}
              </p>
            </div>
            <div>
              <p className="text-xs text-white/40">Preço (USD)</p>
              <p className="text-lg font-bold text-white font-mono">
                {hideCurrency(coinPrice.priceUSD, isVisible, 'USD')}
              </p>
            </div>
            <div>
              <p className="text-xs text-white/40">Var. 24h</p>
              <p className={`text-lg font-bold font-mono ${gainLossClass(coinPrice.change24h)}`}>
                {hidePercent(coinPrice.change24h, isVisible)}
              </p>
            </div>
            <div>
              <p className="text-xs text-white/40">Market Cap (BRL)</p>
              <p className="text-lg font-bold text-white font-mono">
                {coinPrice.marketCapBRL
                  ? `R$ ${(coinPrice.marketCapBRL / 1e9).toFixed(2)}B`
                  : '—'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-white/30 text-sm">Carregando cotação...</p>
        )}
      </div>

      {/* My Crypto Assets */}
      {cryptoPortfolio && cryptoPortfolio.length > 0 ? (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4">Minhas Criptomoedas</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Ativo</th>
                  <th className="text-right">Quantidade</th>
                  <th className="text-right">Preço Médio</th>
                  <th className="text-right">Preço Atual</th>
                  <th className="text-right">Invest.</th>
                  <th className="text-right">Valor Atual</th>
                  <th className="text-right">Lucro/Prej.</th>
                  <th className="text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {cryptoPortfolio.map((asset) => (
                  <tr key={asset.id}>
                    <td>
                      <div>
                        <p className="font-mono font-medium text-white">{asset.code}</p>
                        <p className="text-xs text-white/40">{asset.name}</p>
                      </div>
                    </td>
                    <td className="text-right font-mono text-sm">{asset.quantity}</td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.averagePrice, isVisible)}</td>
                    <td className="text-right font-mono text-sm">
                      {asset.currentPrice ? hideCurrency(asset.currentPrice, isVisible) : '—'}
                    </td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.totalInvested, isVisible)}</td>
                    <td className="text-right font-mono text-sm">{hideCurrency(asset.currentValue, isVisible)}</td>
                    <td className={`text-right font-mono text-sm ${gainLossClass(asset.gainLoss)}`}>
                      {hideCurrency(asset.gainLoss, isVisible)}
                    </td>
                    <td className={`text-right font-mono text-sm ${gainLossClass(asset.gainLossPercent)}`}>
                      {hidePercent(asset.gainLossPercent, isVisible)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-white/30 text-sm">
            Nenhuma criptomoeda cadastrada.
          </p>
          <p className="text-white/20 text-xs mt-1">
            Cadastre ativos do tipo "Criptomoedas" na página de Ativos.
          </p>
        </div>
      )}
    </div>
  );
}
